import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ─── Types ─────────────────────────────────────────────────

export type AnalysisType =
  | "daily_brief"
  | "campaign_health"
  | "budget_allocation"
  | "anomaly_check"
  | "ab_test"
  | "fulfillment_risk"
  | "budget_simulator";

export interface AnalysisRequest {
  type: AnalysisType;
  params?: {
    campaign_ids?: string[];
    date_range?: { start: string; end: string };
    compare_ids?: string[];
  };
}

export interface AIAdvisorReport {
  id: string;
  type: string;
  status: string;
  analysis: string | null;
  structured_output: Record<string, unknown>;
  recommendations: Array<{
    priority: number;
    action: string;
    rationale: string;
    expected_impact: string;
    effort?: string;
  }>;
  risk_alerts: Array<{
    severity: string;
    title: string;
    description: string;
  }>;
  model_used: string;
  tokens_used: number | null;
  cost_usd: number | null;
  requested_by: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface CPLTarget {
  id: string;
  lead_type_id: string;
  vertical: string;
  target_cpl_low: number;
  target_cpl_high: number;
  alert_threshold_pct: number;
  notes: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Analysis type metadata ────────────────────────────────

export const ANALYSIS_TYPE_META: Record<AnalysisType, { label: string; description: string; icon: string }> = {
  daily_brief: {
    label: "Daily Brief",
    description: "Yesterday's performance + 7-day trend summary",
    icon: "sun",
  },
  campaign_health: {
    label: "Campaign Health",
    description: "Per-campaign health assessment with optimization tips",
    icon: "activity",
  },
  budget_allocation: {
    label: "Budget Allocation",
    description: "Recommended budget reallocation across campaigns",
    icon: "pie-chart",
  },
  anomaly_check: {
    label: "Anomaly Check",
    description: "Detects CPL spikes, spend deviations, conversion drops",
    icon: "alert-triangle",
  },
  ab_test: {
    label: "A/B Test",
    description: "Statistical comparison between campaigns/adsets",
    icon: "git-branch",
  },
  fulfillment_risk: {
    label: "Fulfillment Risk",
    description: "Contracts at risk of under-delivery",
    icon: "shield-alert",
  },
  budget_simulator: {
    label: "Budget Simulator",
    description: "Recommended daily budget based on contracts, metrics, and pause schedule",
    icon: "calculator",
  },
};

// ─── Hooks ─────────────────────────────────────────────────

/** Fetch past AI reports */
export function useAIAdvisorReports(limit = 20) {
  return useQuery({
    queryKey: ["ai-advisor-reports", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_advisor_reports")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as AIAdvisorReport[];
    },
  });
}

/** Fetch a single report */
export function useAIAdvisorReport(id: string | null) {
  return useQuery({
    queryKey: ["ai-advisor-report", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("ai_advisor_reports")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw new Error(error.message);
      return data as unknown as AIAdvisorReport;
    },
    enabled: !!id,
  });
}

/** Run a new analysis */
export function useRunAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: AnalysisRequest) => {
      const { data, error } = await supabase.functions.invoke("ai-advisor-analyze", {
        body: request,
      });

      if (error) throw new Error(error.message);
      return data as AIAdvisorReport;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-advisor-reports"] });
    },
  });
}

// ─── CPL Targets ───────────────────────────────────────────

/** Fetch CPL targets */
export function useCPLTargets() {
  return useQuery({
    queryKey: ["cpl-targets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_advisor_cpl_targets")
        .select("*")
        .order("vertical", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as CPLTarget[];
    },
  });
}

/** Upsert a CPL target */
export function useUpsertCPLTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (target: Omit<CPLTarget, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("ai_advisor_cpl_targets")
        .upsert(
          { ...target, updated_at: new Date().toISOString() },
          { onConflict: "lead_type_id" }
        )
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data as unknown as CPLTarget;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cpl-targets"] });
    },
  });
}

/** Delete a CPL target */
export function useDeleteCPLTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("ai_advisor_cpl_targets")
        .delete()
        .eq("id", id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cpl-targets"] });
    },
  });
}
